import type { PublicSnapshot } from "../lib/publication-api";

/**
 * CANONICAL read-only render of an immutable publication snapshot.
 *
 * This is the single presentational path shared by the PUBLIC surface
 * (`PublicPublicationView`, `#p/<id>`) and the admin Review Inspector. The
 * public route feeds it the public snapshot; the admin Inspector feeds it the
 * admin-authorized snapshot (which may be a moderated/hidden publication). The
 * MARKUP is identical either way — the Inspector never renders more than the
 * real public publication content, only from an authorized source.
 */
export function PublicationSnapshotView({ snapshot }: { snapshot: PublicSnapshot }) {
  return (
    <div data-testid="public-pub" data-revision={snapshot.publication_revision} className="p-6">
      <div className="mb-4">
        <div data-testid="public-pub-name" className="text-lg font-display text-neon-teal">
          {snapshot.display_name ?? "Territory"}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-text-secondary">
          Published · revision {snapshot.publication_revision}
        </div>
      </div>
      <ul data-testid="public-pub-elements" className="space-y-1">
        {snapshot.elements.map((el, i) => {
          const type = String((el as { type?: unknown }).type ?? "");
          const text = (el as { text?: unknown }).text;
          return (
            <li key={i} data-testid="public-pub-element" data-type={type} className="font-mono text-sm text-text-primary">
              <span className="text-text-secondary">{type}</span>
              {typeof text === "string" && text.length > 0 ? <span className="ml-2">{text}</span> : null}
            </li>
          );
        })}
        {snapshot.elements.length === 0 && (
          <li data-testid="public-pub-element-empty" className="font-mono text-sm text-text-muted">
            (empty canvas)
          </li>
        )}
      </ul>
    </div>
  );
}
